import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
  type CognitoIdToken,
} from 'amazon-cognito-identity-js';
import type { Rol } from '@condo/contracts';
import { AuthError, type AuthProvider, type AuthSession } from './auth-provider';

/**
 * Provider Cognito real — usa `amazon-cognito-identity-js` para autenticar
 * contra el User Pool del deploy. Los claims custom (`custom:tenants`) se
 * inyectan por el Pre-Token-Generation Lambda del AuthStack.
 *
 * Limitaciones conocidas (MVP):
 *   - Solo soporta flujo USER_SRP_AUTH (username + password).
 *   - El `new_password_required` se propaga como AuthError; el frontend debe
 *     redirigir a una pantalla de "cambiar contraseña" (todavía no implementada).
 *   - MFA no está manejada — el User Pool está con MFA.OPTIONAL.
 */
export class CognitoAuthProvider implements AuthProvider {
  private readonly pool: CognitoUserPool;

  constructor(userPoolId: string, clientId: string) {
    this.pool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
    });
  }

  async signIn(email: string, password: string): Promise<AuthSession> {
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    const details = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<AuthSession>((resolve, reject) => {
      user.authenticateUser(details, {
        onSuccess: (session) => {
          try {
            resolve(toAuthSession(session, email));
          } catch (e) {
            reject(
              e instanceof Error
                ? new AuthError('unknown', e.message)
                : new AuthError('unknown'),
            );
          }
        },
        onFailure: (err: unknown) => {
          const code = mapCognitoError(err);
          reject(new AuthError(code, errorMessage(err)));
        },
        newPasswordRequired: () => {
          reject(new AuthError('new_password_required'));
        },
      });
    });
  }

  async signOut(): Promise<void> {
    const current = this.pool.getCurrentUser();
    if (current) current.signOut();
  }

  async restoreSession(): Promise<AuthSession | null> {
    const current = this.pool.getCurrentUser();
    if (!current) return null;
    return new Promise<AuthSession | null>((resolve) => {
      current.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        try {
          resolve(toAuthSession(session, current.getUsername()));
        } catch {
          resolve(null);
        }
      });
    });
  }
}

/**
 * Parses the ID token payload and builds the neutral `AuthSession` shape.
 *
 * Claims expected (emitted by Pre-Token-Gen Lambda):
 *   - `custom:tenants`  — JSON map { tenantId: rol }
 *   - `cognito:groups`  — ['super_admin'] or [] (HTTP API v2 may serialize as
 *                         string "[super_admin]" — handle both)
 */
export function parseIdToken(idToken: string): {
  sub: string;
  email: string;
  name?: string;
  isSuperAdmin: boolean;
  tenants: Record<string, Rol>;
  exp: number;
} {
  const payload = JSON.parse(atob(idToken.split('.')[1]!)) as Record<string, unknown>;
  const groups = payload['cognito:groups'] ?? [];
  const groupsArr: string[] = Array.isArray(groups)
    ? (groups as string[])
    : String(groups).replace(/^\[|\]$/g, '').split(/[,\s]+/).filter(Boolean);
  let tenants: Record<string, Rol> = {};
  try {
    tenants = JSON.parse((payload['custom:tenants'] as string | undefined) ?? '{}') as Record<string, Rol>;
  } catch {
    /* ignore malformed claim */
  }
  return {
    sub: payload['sub'] as string,
    email: payload['email'] as string,
    name: payload['name'] as string | undefined,
    isSuperAdmin: groupsArr.includes('super_admin'),
    tenants,
    exp: payload['exp'] as number,
  };
}

/**
 * Transforma el `CognitoUserSession` al shape neutro `AuthSession`.
 * Lee los claims del ID token (NO del access token — los custom:* claims
 * no están en access tokens por default).
 */
function toAuthSession(session: CognitoUserSession, fallbackEmail: string): AuthSession {
  const idToken: CognitoIdToken = session.getIdToken();
  const payload = idToken.payload as Record<string, unknown>;

  const email = asString(payload['email']) ?? fallbackEmail;
  const sub = asString(payload['sub']);
  const name = asString(payload['name']) ?? asString(payload['given_name']) ?? email;

  if (!sub) {
    throw new AuthError('unknown', 'ID token sin sub');
  }

  const groups = payload['cognito:groups'] ?? [];
  const groupsArr: string[] = Array.isArray(groups)
    ? (groups as string[])
    : String(groups).replace(/^\[|\]$/g, '').split(/[,\s]+/).filter(Boolean);

  let tenants: Record<string, Rol> = {};
  try {
    tenants = JSON.parse(
      asString(payload['custom:tenants']) ?? '{}',
    ) as Record<string, Rol>;
  } catch {
    /* ignore malformed claim */
  }

  const isSuperAdmin = groupsArr.includes('super_admin');

  if (!isSuperAdmin && Object.keys(tenants).length === 0) {
    throw new AuthError(
      'unknown',
      'ID token sin custom:tenants ni grupo super_admin — el Pre-Token-Generation Lambda no corrió o falló',
    );
  }

  const expSeconds = idToken.getExpiration();

  return {
    userId: sub,
    email,
    nombre: name,
    isSuperAdmin,
    tenants,
    token: idToken.getJwtToken(),
    expiresAt: new Date(expSeconds * 1000).toISOString(),
  };
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function mapCognitoError(err: unknown): AuthError['code'] {
  if (!err || typeof err !== 'object') return 'unknown';
  const name = (err as { code?: string; name?: string }).code ?? (err as { name?: string }).name;
  switch (name) {
    case 'NotAuthorizedException':
      return 'invalid_credentials';
    case 'UserNotFoundException':
      return 'user_not_found';
    case 'NetworkError':
      return 'network_error';
    default:
      return 'unknown';
  }
}

function errorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message?: unknown }).message;
    return typeof msg === 'string' ? msg : undefined;
  }
  return undefined;
}
